import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Syringe, Plus, ChevronLeft, Trash2, Camera, X, Loader2, Edit2, Plane, AlertCircle, CalendarClock, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { Spinner } from "../common";
import AttachmentsPanel from "./AttachmentsPanel";

/**
 * VaccinationsView — Phase 2 (§64) Vaccinations module.
 * Fields captured: name (vaccine), disease, manufacturer, dose_number,
 * total_doses, administered_date, expiry_date, booster_due_date, lot_number,
 * administered_by, location, is_travel_required, notes.
 * Photo attachments via shared sv_attachments (parent_type='vaccination').
 */

const COMMON_VACCINES = [
  "COVID-19", "Influenza (Flu)", "Hepatitis A", "Hepatitis B", "Yellow Fever",
  "Typhoid", "Cholera", "Japanese Encephalitis", "Rabies", "Meningococcal",
  "MMR (Measles/Mumps/Rubella)", "Tdap (Tetanus/Diphtheria/Pertussis)",
  "Polio", "Chickenpox (Varicella)", "HPV", "Pneumococcal", "Shingles",
];

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = new Date().setHours(0,0,0,0);
    return Math.round((then - now) / 86400000);
  } catch { return null; }
}

function VaccinationDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    disease: initial?.disease || "",
    manufacturer: initial?.manufacturer || "",
    dose_number: initial?.dose_number ?? "",
    total_doses: initial?.total_doses ?? "",
    administered_date: initial?.administered_date || "",
    expiry_date: initial?.expiry_date || "",
    booster_due_date: initial?.booster_due_date || "",
    lot_number: initial?.lot_number || "",
    administered_by: initial?.administered_by || "",
    location: initial?.location || "",
    is_travel_required: initial?.is_travel_required || false,
    notes: initial?.notes || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Vaccine name is required");
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        disease: form.disease || null,
        manufacturer: form.manufacturer || null,
        dose_number: form.dose_number === "" ? null : Number(form.dose_number),
        total_doses: form.total_doses === "" ? null : Number(form.total_doses),
        administered_date: form.administered_date || null,
        expiry_date: form.expiry_date || null,
        booster_due_date: form.booster_due_date || null,
        lot_number: form.lot_number || null,
        administered_by: form.administered_by || null,
        location: form.location || null,
        is_travel_required: !!form.is_travel_required,
        notes: form.notes || null,
      };
      if (initial) {
        await api.patch(`/vault/health/vaccinations/${initial.id}`, body);
        toast.success("Vaccination updated");
      } else {
        await api.post("/vault/health/vaccinations", body);
        toast.success("Vaccination recorded");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const inp = "mt-1 w-full border border-zinc-300 px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-[9998] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="vaccination-dialog">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Syringe className="w-5 h-5 text-cyan-600" />
            <h3 className="font-heading font-800 text-lg">{initial ? "Edit vaccination" : "Add vaccination"}</h3>
          </div>
          <button onClick={onClose} data-testid="vaccination-close" className="text-zinc-500 hover:text-destructive"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="font-600">Vaccine name</span>
            <input autoFocus value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="vaccination-name" placeholder="e.g. Pfizer BioNTech COVID-19" className={inp} list="common-vaccines" />
            <datalist id="common-vaccines">{COMMON_VACCINES.map((v) => <option key={v} value={v} />)}</datalist>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Disease</span>
              <input value={form.disease} onChange={(e) => set("disease", e.target.value)} placeholder="COVID-19"
                data-testid="vaccination-disease" className={inp} list="common-vaccines" />
            </label>
            <label className="block text-sm"><span className="font-600">Manufacturer</span>
              <input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} placeholder="Pfizer, Moderna…"
                data-testid="vaccination-manufacturer" className={inp} />
            </label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="block text-sm"><span className="font-600">Dose #</span>
              <input type="number" min="1" max="20" value={form.dose_number} onChange={(e) => set("dose_number", e.target.value)}
                data-testid="vaccination-dose" className={inp} placeholder="1" />
            </label>
            <label className="block text-sm"><span className="font-600">Total doses</span>
              <input type="number" min="1" max="20" value={form.total_doses} onChange={(e) => set("total_doses", e.target.value)}
                data-testid="vaccination-total" className={inp} placeholder="2" />
            </label>
            <label className="block text-sm"><span className="font-600">Given on</span>
              <input type="date" value={form.administered_date} onChange={(e) => set("administered_date", e.target.value)}
                data-testid="vaccination-date" className={inp} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Booster due</span>
              <input type="date" value={form.booster_due_date} onChange={(e) => set("booster_due_date", e.target.value)}
                data-testid="vaccination-booster" className={inp} />
            </label>
            <label className="block text-sm"><span className="font-600">Expiry (if any)</span>
              <input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)}
                data-testid="vaccination-expiry" className={inp} />
            </label>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer font-600 text-zinc-700">More details</summary>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm"><span className="font-600">Lot number</span>
                  <input value={form.lot_number} onChange={(e) => set("lot_number", e.target.value)}
                    data-testid="vaccination-lot" className={inp} />
                </label>
                <label className="block text-sm"><span className="font-600">Location</span>
                  <input value={form.location} onChange={(e) => set("location", e.target.value)}
                    data-testid="vaccination-location" className={inp} placeholder="City / Country" />
                </label>
              </div>
              <label className="block text-sm"><span className="font-600">Administered by</span>
                <input value={form.administered_by} onChange={(e) => set("administered_by", e.target.value)}
                  data-testid="vaccination-admin" className={inp} placeholder="Clinic or physician" />
              </label>
              <label className="block text-sm"><span className="font-600">Notes</span>
                <textarea rows={2} maxLength={500} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  data-testid="vaccination-notes" className={inp} />
              </label>
            </div>
          </details>

          <label className="flex items-center gap-2 text-sm cursor-pointer p-2 border border-sky-200 bg-sky-50">
            <input type="checkbox" checked={form.is_travel_required} onChange={(e) => set("is_travel_required", e.target.checked)}
              data-testid="vaccination-travel" className="w-4 h-4 accent-sky-600" />
            <span><Plane className="w-3.5 h-3.5 inline text-sky-600 mr-1" /> Required for international travel (e.g. Yellow Fever)</span>
          </label>

          <p className="text-[11px] text-muted-foreground leading-snug">
            Attach a photo of the vaccination record card after saving. Records are private to your Health Vault.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="vaccination-save"
            className="px-4 py-2 bg-cyan-600 text-white text-sm font-700 disabled:opacity-60 inline-flex items-center gap-1.5">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {initial ? "Save changes" : "Record vaccination"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VaccinationRow({ v, onEdit, onDelete }) {
  const [openAttach, setOpenAttach] = useState(false);
  const days = daysUntil(v.booster_due_date);
  const isOverdue = days !== null && days < 0;
  const isSoon = days !== null && days >= 0 && days <= 30;
  return (
    <div className="border-b border-zinc-100 last:border-b-0 px-4 py-3" data-testid={`vaccination-row-${v.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-800 text-zinc-900 flex items-center gap-2 flex-wrap">
            <span>{v.name}</span>
            {v.is_travel_required && <span className="text-[9px] font-900 tracking-wider bg-sky-600 text-white px-1.5 py-0.5 inline-flex items-center gap-0.5"><Plane className="w-2.5 h-2.5" /> TRAVEL</span>}
            {isOverdue && <span className="text-[9px] font-900 tracking-wider bg-rose-600 text-white px-1.5 py-0.5">BOOSTER OVERDUE</span>}
            {isSoon && !isOverdue && <span className="text-[9px] font-900 tracking-wider bg-amber-500 text-white px-1.5 py-0.5">BOOSTER SOON</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {v.disease && <span>{v.disease} · </span>}
            Given {fmtDate(v.administered_date)}
            {(v.dose_number || v.total_doses) && <span> · Dose {v.dose_number || "?"}/{v.total_doses || "?"}</span>}
          </div>
          {v.booster_due_date && (
            <div className="text-[11px] text-slate-600 mt-0.5 inline-flex items-center gap-1">
              <CalendarClock className="w-3 h-3" /> Booster due {fmtDate(v.booster_due_date)}
              {days !== null && <span className="text-slate-500">· {isOverdue ? `${Math.abs(days)}d ago` : `in ${days}d`}</span>}
            </div>
          )}
          {(v.administered_by || v.location) && (
            <div className="text-[11px] text-slate-500 mt-0.5">{[v.administered_by, v.location].filter(Boolean).join(" · ")}</div>
          )}
          {v.notes && <div className="text-[11px] text-slate-500 mt-0.5"><em>{v.notes}</em></div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setOpenAttach((x) => !x)} data-testid={`vaccination-attach-${v.id}`} title="Photos" className="p-1.5 text-zinc-400 hover:text-cyan-600"><Camera className="w-4 h-4" /></button>
          <button onClick={() => onEdit(v)} data-testid={`vaccination-edit-${v.id}`} title="Edit" className="p-1.5 text-zinc-400 hover:text-primary"><Edit2 className="w-4 h-4" /></button>
          <button onClick={() => onDelete(v)} data-testid={`vaccination-delete-${v.id}`} title="Remove" className="p-1.5 text-zinc-400 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {openAttach && (
        <div className="mt-3">
          <AttachmentsPanel parentType="vaccination" parentId={v.id} defaultTags={["vaccination", v.disease].filter(Boolean)} compact />
        </div>
      )}
    </div>
  );
}

export default function VaccinationsView({ onBack }) {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/vault/health/vaccinations")
    .then((r) => setItems(r.data.items || []))
    .catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.name} from your vaccinations?`)) return;
    try { await api.delete(`/vault/health/vaccinations/${v.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const upcoming = (items || []).filter((v) => {
    const d = daysUntil(v.booster_due_date);
    return d !== null && d <= 90;
  }).sort((a, b) => (a.booster_due_date || "").localeCompare(b.booster_due_date || ""));

  return (
    <div data-testid="vaccinations-view">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} data-testid="vaccinations-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={() => setAdding(true)} data-testid="vaccinations-add"
          className="px-3 py-2 bg-cyan-600 text-white text-sm font-700 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Record vaccination
        </button>
      </div>

      <div className="bg-gradient-to-br from-cyan-50 to-white border border-zinc-200 p-4 mb-4">
        <div className="flex items-center gap-3">
          <Syringe className="w-6 h-6 text-cyan-600" />
          <div>
            <h1 className="font-heading font-900 text-2xl">Vaccinations</h1>
            <p className="text-xs text-muted-foreground">{items?.length || 0} recorded · attach card photos · flag travel-required</p>
          </div>
        </div>
      </div>

      {upcoming.length > 0 && (
        <div className="border border-amber-200 bg-amber-50/50 p-3 mb-4" data-testid="vaccinations-upcoming">
          <div className="text-[10px] font-800 uppercase tracking-wider text-amber-800 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Boosters due within 90 days</div>
          <ul className="mt-2 space-y-1">
            {upcoming.map((v) => (
              <li key={v.id} className="text-xs flex items-center justify-between">
                <span className="font-700">{v.name}</span>
                <span className="text-slate-600 font-mono">{fmtDate(v.booster_due_date)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {items === null ? <Spinner /> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-8 text-center" data-testid="vaccinations-empty">
          <Syringe className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No vaccinations recorded yet. Tap <strong>Record vaccination</strong> to add COVID-19, flu, yellow fever, or any others.</p>
          <button onClick={() => setAdding(true)} className="px-4 py-2 bg-cyan-600 text-white text-sm font-700 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Record your first vaccination
          </button>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white">
          {items.map((v) => <VaccinationRow key={v.id} v={v} onEdit={setEditing} onDelete={remove} />)}
        </div>
      )}

      <div className="mt-4 border border-emerald-200 bg-emerald-50/50 p-3 text-[11px] text-emerald-900 leading-relaxed flex items-start gap-2" data-testid="vaccinations-privacy-note">
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span><b>Private by default.</b> Your vaccination records — and any attached card photos — are isolated from ads, analytics, and community feeds. Only you can access them.</span>
      </div>

      {(adding || editing) && <VaccinationDialog initial={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={load} />}
    </div>
  );
}
